import {GroupCaseDetails} from 'ApplicationEntry/models/GroupCaseDetails';
import {NewYorkCaseDetails} from 'ApplicationEntry/models/NewYorkCaseDetails';
import {OrdinaryCaseDetails} from 'ApplicationEntry/models/OrdinaryCaseDetails';
import {ProductRiderGroups, ProductsRidersPolicyType} from './ProductsRiders/Families';
import {Product} from './ProductsRiders/PlanModel';

export type CaseDetails = OrdinaryCaseDetails | GroupCaseDetails | NewYorkCaseDetails;

type ExhaustiveCaseDetailsOptionsByType<TReturn> = {
  Group: (group: GroupCaseDetails) => TReturn;
  Ordinary: (ordinary: OrdinaryCaseDetails) => TReturn;
  NewYork: (newYork: NewYorkCaseDetails) => TReturn;
};

type PartialCaseDetailsOptionsByType<TReturn> = {
  Group?: (group: GroupCaseDetails) => TReturn;
  Ordinary?: (ordinary: OrdinaryCaseDetails) => TReturn;
  NewYork?: (newYork: NewYorkCaseDetails) => TReturn;
  _: (app: CaseDetails) => TReturn;
};

const isExhaustiveByType = <TReturn>(
  options: CaseDetailsByTypeOptions<TReturn>
): options is ExhaustiveCaseDetailsOptionsByType<TReturn> => !!options.Group && !!options.NewYork && !!options.Ordinary;

type CaseDetailsByTypeOptions<TReturn> =
  | PartialCaseDetailsOptionsByType<TReturn>
  | ExhaustiveCaseDetailsOptionsByType<TReturn>;

export const matchCaseDetailsByType = <TReturn>(options: CaseDetailsByTypeOptions<TReturn>) => (
  app: CaseDetails
): TReturn => {
  switch (app.key) {
    case 'InvalidGroupCaseDetails':
    case 'ValidGroupCaseDetails':
      return isExhaustiveByType(options) ? options.Group(app) : options.Group ? options.Group(app) : options._(app);
    case 'InvalidNewYorkCaseDetails':
    case 'ValidNewYorkCaseDetails':
      return isExhaustiveByType(options)
        ? options.NewYork(app)
        : options.NewYork
        ? options.NewYork(app)
        : options._(app);
    case 'InvalidOrdinaryCaseDetails':
    case 'ValidOrdinaryCaseDetails':
      return isExhaustiveByType(options)
        ? options.Ordinary(app)
        : options.Ordinary
        ? options.Ordinary(app)
        : options._(app);
  }
};

export const isValidCaseDetails = (caseDetails: CaseDetails) =>
  caseDetails.key === 'ValidGroupCaseDetails' ||
  caseDetails.key === 'ValidNewYorkCaseDetails' ||
  caseDetails.key === 'ValidOrdinaryCaseDetails';

const productFamilyGroups = [
  [
    {name: ProductRiderGroups[ProductsRidersPolicyType.CriticalIllness]},
    {name: ProductRiderGroups[ProductsRidersPolicyType.CriticalIllnessNY]},
    {name: ProductRiderGroups[ProductsRidersPolicyType.Gvcip4]},
  ],
  [{name: ProductsRidersPolicyType.Accident}, {name: ProductsRidersPolicyType.AccidentNewYork}],
  [
    {name: ProductRiderGroups[ProductsRidersPolicyType.UniversalLife]},
    {name: ProductRiderGroups[ProductsRidersPolicyType.TermLife]},
    {name: ProductRiderGroups[ProductsRidersPolicyType.Iwl]},
    {name: ProductRiderGroups[ProductsRidersPolicyType.GLTerm]},
    {name: ProductRiderGroups[ProductsRidersPolicyType.Gwl]},
  ],
  [
    {name: ProductRiderGroups[ProductsRidersPolicyType.Disability]},
    {name: ProductRiderGroups[ProductsRidersPolicyType.DisabilityNC]},
  ],
];

export const getProductFamilyGroups = (): Product[][] => {
  return productFamilyGroups;
};

export const getProductFamilyByIndex = (index: number, policy: Product): Product[] => {
  const productGrp = productFamilyGroups[index];
  const filteredGrp = productGrp.filter(product => product !== policy);
  return filteredGrp;
};

export const getAllLifeProducts: Product[] = [
  {productGroup: ProductRiderGroups[ProductsRidersPolicyType.UniversalLife]},
  {productGroup: ProductRiderGroups[ProductsRidersPolicyType.TermLife]},
  {productGroup: ProductRiderGroups[ProductsRidersPolicyType.Iwl]},
  {productGroup: ProductRiderGroups[ProductsRidersPolicyType.GLTerm]},
  {productGroup: ProductRiderGroups[ProductsRidersPolicyType.Gwl]},
];

export const getAllHealthProducts: Product[] = [
  {productGroup: ProductRiderGroups[ProductsRidersPolicyType.Cancer]},
  {productGroup: ProductRiderGroups[ProductsRidersPolicyType.Accident]},
  {productGroup: ProductRiderGroups[ProductsRidersPolicyType.CriticalIllness]},
  {productGroup: ProductRiderGroups[ProductsRidersPolicyType.Shop]},
];
